import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CreditCard as CreditCardIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import apiService from '../../services/api';
import { SubscriptionPlan, CreateSubscriptionPlanRequest, UpdateSubscriptionPlanRequest } from '../../types';

const SubscriptionPlansPage: React.FC = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    duration_days: 30,
    credits_limit: null as number | null,
    is_active: true,
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await apiService.getSubscriptionPlans();
      setPlans(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Error fetching subscription plans:', err);
      setError(err.response?.data?.message || 'Failed to load subscription plans');
      setPlans([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (plan?: SubscriptionPlan) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        description: plan.description || '',
        price: typeof plan.price === 'string' ? parseFloat(plan.price) : plan.price,
        duration_days: plan.duration_days,
        credits_limit: plan.credits_limit,
        is_active: plan.is_active,
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: '',
        description: '',
        price: 299,
        duration_days: 30,
        credits_limit: null,
        is_active: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPlan(null);
    setFormData({
      name: '',
      description: '',
      price: 299,
      duration_days: 30,
      credits_limit: null,
      is_active: true,
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingPlan) {
        const updateData: UpdateSubscriptionPlanRequest = {
          name: formData.name,
          description: formData.description,
          price: formData.price,
          duration_days: formData.duration_days,
          credits_limit: formData.credits_limit,
          is_active: formData.is_active,
        };
        await apiService.updateSubscriptionPlan(editingPlan.id, updateData);
      } else {
        const createData: CreateSubscriptionPlanRequest = {
          name: formData.name,
          description: formData.description,
          price: formData.price,
          duration_days: formData.duration_days,
          credits_limit: formData.credits_limit,
          is_active: formData.is_active,
        };
        await apiService.createSubscriptionPlan(createData);
      }
      
      handleCloseDialog();
      fetchPlans();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save subscription plan');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this subscription plan? This action cannot be undone.')) {
      try {
        await apiService.deleteSubscriptionPlan(id);
        fetchPlans();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete subscription plan');
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
    },
    {
      field: 'name',
      headerName: 'Plan Name',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <CreditCardIcon color="primary" />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'price',
      headerName: 'Price (₹)',
      width: 120,
      renderCell: (params) => {
        const price = typeof params.value === 'string' ? parseFloat(params.value) : params.value;
        return `₹${(price || 0).toFixed(2)}`;
      },
    },
    {
      field: 'duration_days',
      headerName: 'Duration',
      width: 120,
      renderCell: (params) => `${params.value} days`,
    },
    {
      field: 'credits_limit',
      headerName: 'Credits Limit',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value === null ? 'Unlimited' : params.value}
        </Typography>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label={params.value ? 'Active' : 'Inactive'}
            color={params.value ? 'success' : 'default'}
            size="small"
          />
          {!params.value && (
            <Chip
              label="Hidden"
              color="error"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      ),
    },
    {
      field: 'active_subscriptions_count',
      headerName: 'Active Subs',
      width: 120,
      renderCell: (params) => params.value || 0,
    },
    {
      field: 'total_revenue',
      headerName: 'Revenue (₹)',
      width: 150,
      renderCell: (params) => {
        if (!params.value) return '₹0.00';
        const revenue = typeof params.value === 'string' ? parseFloat(params.value) : params.value;
        return `₹${(revenue || 0).toFixed(2)}`;
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      renderCell: (params) => new Date(params.value).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDelete(params.row.id)}
        />,
      ],
    },
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Subscription Plans</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Plan
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <DataGrid
            rows={Array.isArray(plans) ? plans : []}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25]}
            disableRowSelectionOnClick
            autoHeight
          />
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPlan ? 'Edit Subscription Plan' : 'Add Subscription Plan'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Plan Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Price (₹)"
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                  margin="normal"
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Duration (Days)"
                  type="number"
                  value={formData.duration_days}
                  onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) || 30 })}
                  margin="normal"
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Credits Limit"
              type="number"
              value={formData.credits_limit === null ? '' : formData.credits_limit}
              onChange={(e) => setFormData({ 
                ...formData, 
                credits_limit: e.target.value === '' ? null : parseInt(e.target.value) 
              })}
              margin="normal"
              helperText="Leave empty for unlimited credits"
              inputProps={{ min: 1 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  color="success"
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography>Active</Typography>
                  {formData.is_active ? (
                    <Chip label="Visible to Patients" color="success" size="small" />
                  ) : (
                    <Chip label="Hidden from Patients" color="error" size="small" />
                  )}
                </Box>
              }
              sx={{ mt: 2 }}
            />
            <Alert severity={formData.is_active ? 'success' : 'warning'} sx={{ mt: 1 }}>
              {formData.is_active 
                ? 'This plan will be visible to patients and available for subscription.'
                : 'This plan will be hidden from patients. Only active plans are shown in the subscription page.'}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingPlan ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionPlansPage;

